import { Button, Drawer, Text, toast } from "@medusajs/ui"
import { useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { ACCEPT, fileSize, isImage, uploadMany, useFiles, type MediaFile } from "@/lib/files"
import { useCan } from "@/lib/permissions"
import { FileThumb, SearchInput } from "./ui"

export function FileTile({ f, selected, onClick, children }: { f: MediaFile; selected?: boolean; onClick?: () => void; children?: React.ReactNode }) {
  return (
    <div className={"group relative overflow-hidden rounded-lg border bg-ui-bg-base " + (selected ? "border-[var(--tb-ink)] ring-1 ring-[var(--tb-ink)]" : "border-ui-border-base")}>
      <button type="button" onClick={onClick} className="block w-full text-left" aria-label={`${f.filename}${selected ? ", selected" : ""}`}>
        <div className="flex aspect-square items-center justify-center bg-ui-bg-subtle">
          {isImage(f) ? (
            <img src={f.url} alt={f.alt ?? ""} loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <FileThumb mime={f.mime_type} src={f.url} size={64} />
          )}
        </div>
        <div className="px-2.5 py-2">
          <div className="truncate text-[13px] font-medium">{f.filename}</div>
          <div className="text-[12px] text-ui-fg-subtle">{fileSize(f.size)}</div>
        </div>
      </button>
      {children}
    </div>
  )
}

/** "Choose from Files": pick existing files or upload new ones, then confirm. */
export function FilePicker({ images = true, multiple = false, onDone, onClose, title }: { images?: boolean; multiple?: boolean; onDone: (files: MediaFile[]) => void; onClose: () => void; title?: string }) {
  const [q, setQ] = useState("")
  const [picked, setPicked] = useState<MediaFile[]>([])
  const [uploading, setUploading] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const can = useCan()
  const qc = useQueryClient()
  const { data, isLoading } = useFiles({ q, type: images ? "image" : undefined, limit: 60, offset: 0 })
  const toggle = (f: MediaFile) =>
    setPicked((p) => (p.some((x) => x.id === f.id) ? p.filter((x) => x.id !== f.id) : multiple ? [...p, f] : [f]))
  const upload = async (list: File[]) => {
    if (!list.length) return
    setUploading(true)
    const { done, failed } = await uploadMany(list)
    setUploading(false)
    failed.forEach((f) => toast.error(`${f.name}: ${f.error}`))
    if (done.length) {
      qc.invalidateQueries({ queryKey: ["files"] })
      setPicked((p) => (multiple ? [...p, ...done] : [done[0]]))
    }
  }
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content className="max-w-[640px]">
        <Drawer.Header>
          <Drawer.Title>{title ?? "Select file"}</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-3 overflow-y-auto">
          <div className="flex items-center gap-x-2">
            <div className="flex-1"><SearchInput value={q} onChange={setQ} placeholder="Search files" /></div>
            {can("content:write") && (
              <>
                <Button size="small" variant="secondary" isLoading={uploading} onClick={() => input.current?.click()}>Upload</Button>
                <input ref={input} type="file" hidden multiple={multiple} accept={images ? "image/jpeg,image/png,image/webp,image/gif,image/avif" : ACCEPT} onChange={(e) => { upload(Array.from(e.target.files ?? [])); e.target.value = "" }} />
              </>
            )}
          </div>
          {isLoading && <Text size="small" className="text-ui-fg-muted">Loading…</Text>}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {data?.files.map((f) => <FileTile key={f.id} f={f} selected={picked.some((x) => x.id === f.id)} onClick={() => toggle(f)} />)}
          </div>
          {data && !data.files.length && <Text size="small" className="py-8 text-center text-ui-fg-muted">{q ? "No files match." : "No files yet. Upload one to get started."}</Text>}
        </Drawer.Body>
        <Drawer.Footer>
          <Text size="small" className="mr-auto text-ui-fg-subtle">{picked.length} selected</Text>
          <Button size="small" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!picked.length} onClick={() => onDone(picked)}>Select</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}
